<template>
    <tr class="lms__superadmin_users_list_table_item">
        <td class="lms__superadmin_users_list_table_item_firstname">
            <div class="columns is-vcentered">
                <div class="column">
                    {{ userFirstName }}
                </div>
            </div>
        </td>
        <td class="lms__superadmin_users_list_table_item_lastname">
            <div class="columns is-vcentered">
                <div class="column">
                    {{ userLastName }}
                </div>
            </div>
        </td>
        <td class="lms__superadmin_users_list_table_item_email">
            {{ userEmail }}
            <template v-if="userIsSuperadmin && user.isSuperadmin() || userIsMasteradmin">
                <div class="lms__superadmin_users_list_table_item_email_roles">
                    <component-badge-role
                        v-if="userIsSuperadmin && user.isSuperadmin()"
                        :type="'third'"
                        :role="'superadmin'"
                    ></component-badge-role>
                    <component-badge-role
                        v-if="userIsMasteradmin"
                        :type="'second'"
                        :role="'master_administrator'"
                    ></component-badge-role>
                </div>
            </template>
        </td>
        <template v-if="loggedInUserWorkspaceId">
            <td class="lms__superadmin_users_list_table_item_roles">
                <component-badge-roles-list
                    v-if="userProfileRoles"
                    :roles="userProfileRoles"
                ></component-badge-roles-list>
            </td>
            <td class="lms__superadmin_users_list_table_item_orgunit">
                {{ userProfileOrgUnitName }}
            </td>
            <td class="lms__superadmin_users_list_table_item_group">
                <component-text-list
                    :items="userProfileGroups"
                    :limit-active="true"
                ></component-text-list>
            </td>
            <td class="lms__superadmin_users_list_table_item_status">
                <component-status :status="userProfileStatusOutput"/>
            </td>
        </template>
        <td class="lms__superadmin_users_list_table_item_actions">
            <div class="columns is-vcentered">
                <div class="column"></div>
                <template v-if="userProfiles && userProfiles.length && !loggedInUserWorkspaceId">
                    <div class="column is-narrow">
                        <component-button
                            :name="'dashboard-list-table-item-collapse'"
                            :icon="true"
                            :icon-name="collapseActive ? 'chevron-up' : 'chevron-down'"
                            :theme="'default'"
                            @click="performEmit('emit-collapse', index)"
                        />
                    </div>
                </template>
                <template v-if="(!loggedInUserWorkspaceId && userProfiles && userProfiles.length) || (loggedInUserWorkspaceId && (userProfileStatus !== 'invited' && userProfileStatus !== 'pending'))">
                    <div class="column is-narrow">
                        <component-button
                            :name="`lms-superadmin-user-list-table-item-actions-history`"
                            :theme="'default'"
                            :to="{ name: 'lms-superadmin-user-learning-history', params: { userId: userId, workspaceId: loggedInUserWorkspaceId } }"
                        >History
                        </component-button>
                    </div>
                </template>
                <div class="column is-narrow">
                    <component-button-menu
                        :buttons="listMenuButtons"
                        :name="`lms-superadmin-user-list-table-item-actions-menu`"
                        @emit-edit="performRedirectToEdit"
                        @emit-login-as-learner="performEmit('emit-login-as-learner', userProfileId)"
                        @emit-reset-password="performEmit('emit-reset-password', userId)"
                        @emit-reset-mfa="performEmit('emit-reset-mfa', userId)"
                        @emit-remove-superadmin="performEmit('emit-remove-superadmin', userId)"
                        @emit-remove-masteradmin="performEmit('emit-remove-masteradmin', userId)"
                        @emit-resend-invitation="performEmit('emit-resend', userProfileId)"
                        @emit-cancel-invitation="performEmit('emit-cancel', userProfileId)"
                        @emit-delete-profile="performEmit('emit-delete-profile', userProfileId)"
                        @emit-reactivate-profile="performEmit('emit-reactivate-profile', { id: userProfileId, status: userProfileStatus})"
                    ></component-button-menu>
                </div>
            </div>
        </td>
    </tr>
</template>

<script>
import ComponentButton from '@/components/default/shared/ComponentButton'
import ComponentStatus from '@/components/default/shared/ComponentStatus'
import { User } from '@/User'
import ComponentButtonMenu from '@/components/default/shared/ComponentButtonMenu'
import ComponentBadgeRole from '@/components/default/shared/ComponentBadgeRole'
import ComponentBadgeRolesList from '@/components/default/shared/ComponentBadgeRolesList'
import ComponentTextList from '@/components/default/shared/ComponentTextList'

export default {
  name: 'lms-superadmin-users-list-table-item',
  components: { ComponentTextList, ComponentBadgeRolesList, ComponentBadgeRole, ComponentButtonMenu, ComponentStatus, ComponentButton },
  data () {
    return {
      user: new User()
    }
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    index: {
      type: [Number, String]
    },
    collapseActive: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    loggedInUserProfilesWithMAorUARoles () {
      return this.$store.getters.getLmsSuperadminUserProfilesWithUAOrMARole
    },
    loggedInUserWorkspaceId () {
      return this.$store.getters.getAuthUserSecondStageTokenDataWorkspaceId
    },
    userId () {
      return this.item.id
    },
    userFirstName () {
      return this.item.firstName
    },
    userLastName () {
      return this.item.lastName
    },
    userEmail () {
      return this.item.email || this.item.emailAddress
    },
    userProfileStatus () {
      let firstProfile
      let status

      if (this.loggedInUserWorkspaceId) {
        status = this.item.profileStatus
      } else {
        firstProfile = this.userProfiles[0]
        status = firstProfile ? firstProfile.status : ''
      }

      return status
    },
    userProfileStatusOutput () {
      const status = this.userProfileStatus
      let output
    
      if (status === 'cancelled') {
        output = 'invitation-cancelled'
      } else if (status === 'deleted') {
        output = 'archived'
      } else {
        output = status
      }
    
      return output
    },
    userProfileId () {
      let firstProfile
      let id

      if (this.loggedInUserWorkspaceId) {
        id = this.item.profileId
      } else {
        firstProfile = this.userProfiles[0]
        id = firstProfile ? firstProfile.id : ''
      }
      return id
    },
    userProfileRoles () {
      let firstProfile
      let roles

      if (this.loggedInUserWorkspaceId) {
        roles = this.userRoles
      } else {
        firstProfile = this.userProfiles[0]
        roles = JSON.parse(JSON.stringify(firstProfile ? firstProfile.roles : []))
      }

      return roles
    },
    userProfileOrgUnitName () {
      const orgUnit = this.item.organisationUnit
      return orgUnit ? orgUnit.name : ''
    },
    userProfileGroups () {
      const groups = this.item.groups
      return groups ? groups.map(obj => obj.name) : []
    },
    userProfiles () {
      return this.item.profiles || []
    },
    userRoles () {
      return this.item.roles
    },
    userCustomerRoles () {
      return this.item.customerRoles
    },
    userInvitations () {
      return this.item.invitations
    },
    userProfileWorkspaceId () {
      let firstProfile
      let workspaceId

      if (this.loggedInUserWorkspaceId) {
        workspaceId = this.loggedInUserWorkspaceId
      } else {
        firstProfile = this.userProfiles[0]
        workspaceId = firstProfile ? firstProfile.workspaceId : ''
      }

      return workspaceId
    },
    userIsSuperadmin () {
      return this.userRoles ? this.userRoles.includes('superadmin') : false
    },
    userIsMasteradmin () {
      return this.userCustomerRoles ? this.userCustomerRoles.includes('master_administrator') : false
    },
    userIsLearner () {
      return this.userProfileRoles ? this.userProfileRoles.includes('learner') : false
    },
    userLatestInvitation () {
      return this.item.latestInvitation
    },
    listMenuButtons () {
      const buttonsArr = []

      if (this.loggedInUserWorkspaceId) {
        buttonsArr.push({
          name: 'Edit',
          key: 'edit'
        })
        if (
          (
            this.user.isSuperadmin() ||
        this.user.isUserAdministrator()
          ) &&
        this.userIsLearner &&
        this.userProfileStatus === 'active' &&
        this.userProfileWorkspaceId === this.loggedInUserWorkspaceId &&
          this.userId !== this.user.userId
        ) {
          buttonsArr.push({
            name: 'Login as learner',
            key: 'login-as-learner'
          })
        }
      }
      if (this.userIsSuperadmin && this.user.isSuperadmin()) {
        buttonsArr.push({
          name: 'Remove superadmin',
          key: 'remove-superadmin'
        })
      }
      if (this.userIsMasteradmin && (this.user.isMasterAdministrator() || this.user.isSuperadmin())) {
        buttonsArr.push({
          name: 'Remove master administrator',
          key: 'remove-masteradmin'
        })
      }
      if (this.userProfileStatus === 'active') {
        buttonsArr.push({
          name: 'Reset password',
          key: 'reset-password'
        })
        if (this.user.isSuperadmin()) {
          buttonsArr.push({
            name: 'Reset 2FA',
            key: 'reset-mfa'
          })
        }
      }
      if (this.loggedInUserWorkspaceId) {
        if (this.userProfileStatus === 'invited' || this.userProfileStatus === 'rejected' || this.userProfileStatus === 'cancelled' || this.userProfileStatus === 'sent') {
          if (buttonsArr && buttonsArr.length) {
            buttonsArr.push({
              name: '',
              key: '',
              blank: true
            })
          }
          buttonsArr.push({
            name: 'Resend invitation',
            key: 'resend-invitation',
            icon: true,
            iconName: 'mail'
          })
        }
        if (this.userProfileStatus === 'invited' || this.userProfileStatus === 'sent') {
          if (buttonsArr && buttonsArr.length) {
            buttonsArr.push({
              name: '',
              key: '',
              blank: true
            })
          }
          buttonsArr.push({
            name: 'Cancel invitation',
            key: 'cancel-invitation',
            icon: true,
            iconName: 'x-circle'
          })
        }
        if (this.userProfileStatus === 'active') {
          if (buttonsArr && buttonsArr.length) {
            buttonsArr.push({
              name: '',
              key: '',
              blank: true
            })
          }
          buttonsArr.push({
            name: 'Delete profile',
            key: 'delete-profile',
            icon: true,
            iconName: 'trash-2',
            danger: true
          })
        }
        if (this.userProfileStatus === 'deleted' || this.userProfileStatus === 'expired') {
          if (buttonsArr && buttonsArr.length) {
            buttonsArr.push({
              name: '',
              key: '',
              blank: true
            })
          }
          buttonsArr.push({
            name: 'Re-activate profile',
            key: 'reactivate-profile',
            icon: true,
            iconName: 'refresh-ccw'
          })
        }
      }

      return buttonsArr
    }
  },
  methods: {
    performEmit (message, value) {
      this.$emit('emit', { message: message, value: value })
    },
    performRedirectToEdit () {
      if (this.loggedInUserWorkspaceId) {
        this.$router.push({ name: 'lms-superadmin-user-profile-edit', params: { userId: this.userId, profileId: this.userProfileId } })
      } else {
        this.$router.push({ name: 'lms-superadmin-user-edit', params: { userId: this.userId } })
      }
    }
  }
}
</script>
